import * as R from 'ramda';
import {
  useEffect,
  useRef,
  useState,
  useLayoutEffect,
  useCallback,
} from 'react';
import {
  useWindowSize,
  useMount,
  useMountedState as useIsMounted,
  useLocalStorage,
} from 'react-use';
import { getEnv } from './utils';
import { useStoreState } from 'easy-peasy';
import throttle from 'lodash/throttle';
import debounce from 'lodash/debounce';
import { useUserProductsTradeActions, useProducts } from '~/hooks/useProducts';
import { getColumnFieldLabel } from './utils';

export function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

export function useBeforeUnload() {
  function listener(e) {
    // Ref https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload#Example
    e.preventDefault();
    e.returnValue = '';
  }
  useEffect(() => {
    if (!getEnv(import.meta.env.VITE_APP_APPLY_BEFOREUNLOAD)) return;
    window.addEventListener('beforeunload', listener);
    return () => window.removeEventListener('beforeunload', listener);
  });
}

export function useSiteTitle(title) {
  // useTitle(title ? `${title} - Nui Markets` : 'Nui Markets');
}

export function useInterval(callback, delay) {
  const savedCallback = useRef();

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

export function useDrawerWidth() {
  const { width } = useWindowSize();
  return Math.min(width, 600);
}

// export function useCallAsync(fn) {
//   const [run, setRun] = useState(false);
//   const state = useAsync(async () => {
//     if (!run) return Promise.resolve({ loading: false, value: undefined });
//     return fn();
//   }, [run]);
//   // When the handler is called, trigger the async
//   return {
//     state,
//     call: () => {
//       setRun(true);
//     },
//   };
// }

// export function useAsyncPersist(callback, watch) {
//   const [data, setData] = useState(undefined);

//   const result = useAsync(callback, watch);

//   // After API call
//   useEffect(() => {
//     if (!result.loading && !result.error && result.value) {
//       setData(result.value);
//     }
//   }, [result]);

//   return {
//     loading: result.loading,
//     error: result.error,
//     value: data,
//   };
// }

export function useAsyncCall(callback, wait) {
  const [loading, setLoading] = useState(false);
  const [update, setUpdate] = useState(!wait);
  const [value, setValue] = useState();
  const [error, setError] = useState();

  const caller = async () => {
    try {
      const result = await callback();
      setValue(result);
    } catch (e) {
      setError(e);
    }
    setUpdate();
    setLoading(false);
  };

  useEffect(() => {
    if (loading || !update) return;
    setLoading(true);
    caller();
  }, [update]);

  const softUpdate = () => {
    setUpdate(true);
  };

  const hardUpdate = () => {
    if (loading) return;
    setValue(null);
    setUpdate(true);
  };

  return [{ loading, error, value }, softUpdate, hardUpdate];
}

// export function useAsyncPing(apiCallback, interval) {
//   const [data, update] = useAsyncCall(apiCallback);
//   useInterval(update, interval);
//   return data;
// }

// function useOnce(action) {
//   const [hasRun, setHasRun] = useState(false);
//   useEffect(() => {
//     setHasRun(false);
//   }, [action]);
//   const run = (...params) => {
//     if (!hasRun) {
//       setHasRun(true);
//       return action(...params);
//     }
//   };
//   return run;
// }

// export function useCountdown(target, after, accuracy = 500) {
//   const getDiff = tgt => tgt && dayjs.duration(tgt.diff(dayjs()));
//   const [diff, setDiff] = useState(getDiff(target));
//   const doAfter = useOnce(after);

//   useInterval(() => {
//     if (diff) {
//       setDiff(getDiff(target));
//       if (diff.asMilliseconds() <= 0) doAfter();
//     }
//   }, accuracy);

//   return diff;
// }

// Set global timezone to tz, update when args changes. Restore to prev timezone when exit.
// Note that setDefault is not executed in the first round of render, may not set already the correct timezone.
export function useTimezone(tz, args = []) {
  // const [prevTz] = useState(R.path(['defaultZone', 'name'], dayjs));
  // useEffect(() => {
  //   dayjs.tz.setDefault(tz);
  //   return () => void dayjs.tz.setDefault(prevTz);
  // }, [tz, ...args]);
}

// Call fn when window scroll
export function useWindowScrollFn(fn) {
  const frame = useRef(0);
  const [state, setState] = useState();
  useMount(() => {
    const handler = () => {
      cancelAnimationFrame(frame.current);
      frame.current = requestAnimationFrame(() => {
        setState(fn());
      });
    };

    window.addEventListener('scroll', handler, {
      capture: false,
      passive: true,
    });

    return () => {
      cancelAnimationFrame(frame.current);
      window.removeEventListener('scroll', handler);
    };
  });
  return state;
}

export function useIsMobile() {
  const { width } = useWindowSize();
  return width < 576;
}

export function useThrottle(fn, wait, config) {
  const ref = useRef(throttle(fn, wait, config));
  return ref.current;
}

export function useDebounce(fn, wait, config) {
  const ref = useRef(debounce(fn, wait, config));
  return ref.current;
}

export const useScrollToTopOnMount = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);
};

export const useBodyClassReverse = () => {
  useEffect(() => {
    document.body.classList.add('reverse');
    return () => document.body.classList.remove('reverse');
  }, []);
};

export const useLockBodyScroll = () => {
  useLayoutEffect(() => {
    const originalTop = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${originalTop}px`;
    requestAnimationFrame(() => (document.body.style.overflow = ''));

    return () => {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      requestAnimationFrame(() =>
        window.scrollTo(0, parseInt(scrollY || '0') * -1)
      );
    };
  }, []);
};

export function useMountedState(initial) {
  const [state, setState] = useState(initial);
  const isMounted = useIsMounted();

  const update = (next) => {
    if (isMounted()) setState(next);
  };

  return [state, update];
}

export function useLocalJsonStorage(id, initial = {}) {
  const [state, setState, remove] = useLocalStorage(id, '{}', {
    raw: false,
    serializer: JSON.stringify,
    deserializer: JSON.parse,
  });

  return [R.mergeRight(initial, state), setState, remove];
}

export function useDebounced(fn, wait, { unmount = false }, deps) {
  const timerRef = useRef(null);
  const argsRef = useRef(null);
  const debounced = useCallback(
    (...args) => {
      argsRef.current = args;
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        fn(...argsRef.current);
      }, wait);
    },
    [fn, wait, ...deps]
  );
  const instant = useCallback(
    (...args) => {
      argsRef.current = args;
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
      fn(...argsRef.current);
    },
    [fn, wait, ...deps]
  );
  const cancel = useCallback(() => {
    argsRef.current = null;
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
  }, [fn, wait, ...deps]);
  useEffect(
    () => () => {
      if (unmount && timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
        fn(...argsRef.current);
      }
    },
    []
  );
  return [debounced, instant, cancel];
}

export function useOrderPermissions(order, product) {
  const solutionSettings = useStoreState(
    (state) => state.auth.solutionSettings
  );
  const productsTradeActions = useUserProductsTradeActions();

  // Check base product trade actions
  let tradeActions = productsTradeActions?.[product?.id] ?? {};

  // Determine the required permission based on order type
  const requiredAction = order?.type === 'offer' ? 'buy' : 'sell';

  // If base product lacks the required permission, check variant products
  if (!tradeActions?.[requiredAction] && order?.variants?.length) {
    // First, try to find a variant with the specific required permission
    for (const variant of order.variants) {
      const variantTradeActions = productsTradeActions?.[variant.product?.id];
      if (variantTradeActions?.[requiredAction]) {
        tradeActions = variantTradeActions;
        break;
      }
    }
    // Fallback: if still no required permission, find any variant with buy/sell
    if (!tradeActions?.[requiredAction]) {
      for (const variant of order.variants) {
        const variantTradeActions = productsTradeActions?.[variant.product?.id];
        if (variantTradeActions?.buy || variantTradeActions?.sell) {
          tradeActions = variantTradeActions;
          break;
        }
      }
    }
  }

  const canBuy = tradeActions?.buy;
  const canSell = tradeActions?.sell;

  return {
    trade: true,
    qa:
      solutionSettings.orderquestions &&
      (order?.is_owner || (order?.type === 'offer' ? canBuy : canSell)),
    decline:
      order?.type === 'bid' &&
      (solutionSettings?.biddecline ?? true) &&
      tradeActions?.allowdecline,
    counter:
      order?.type === 'offer'
        ? (solutionSettings?.counterbids ?? true) && canBuy && !order?.is_owner
        : (solutionSettings?.counteroffers ?? true) &&
          canSell &&
          !order?.is_owner,
    counters: true,
  };
}

export const useLabels = (record = null) => {
  const solutionColumns = useStoreState((state) => state.auth.solutionColumns);
  const { data: products = [] } = useProducts();
  const product = products?.find((p) => p.id === record?.product?.id);
  const productColumns = product?.columns;

  const tradeToBidType = (recordType) =>
    ({ purchase: 'bid', sale: 'bid' })[recordType] ?? recordType;

  const label = (key, fallback) => {
    let ret =
      record?.__metadata__?.[key]?.label ??
      getColumnFieldLabel(
        tradeToBidType(record?.type),
        key
      )(productColumns ?? solutionColumns);
    if (!ret) {
      ret = solutionColumns?.[key]?.label;
    }
    return ret || fallback;
  };

  return label;
};
